import React, { useState, useEffect } from 'react';
import { reportAPI } from '../services/api';

const ReportScheduler = ({ user, onClose }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    report_name: '',
    report_type: 'overview',
    period: 'week',
    recipient_email: '',
    recipient_type: 'hospital_admin',
    hospital_id: null,
    schedule_day: 'monday',
    schedule_time: '08:00',
    is_active: true
  });
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSchedules();
  }, []);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const response = await reportAPI.getSchedules();
      setSchedules(response.data.schedules || []);
    } catch (error) {
      console.error('Error loading schedules:', error);
      setMessage({ type: 'error', text: 'Failed to load schedules' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchedule) {
        await reportAPI.updateSchedule(editingSchedule.id, formData);
        setMessage({ type: 'success', text: 'Schedule updated successfully' });
      } else {
        await reportAPI.createSchedule(formData);
        setMessage({ type: 'success', text: 'Schedule created successfully' });
      }
      setShowForm(false);
      setEditingSchedule(null);
      resetForm();
      loadSchedules();
    } catch (error) {
      console.error('Error saving schedule:', error);
      setMessage({ type: 'error', text: 'Failed to save schedule' });
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return;
    try {
      await reportAPI.deleteSchedule(scheduleId);
      setMessage({ type: 'success', text: 'Schedule deleted successfully' });
      loadSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      setMessage({ type: 'error', text: 'Failed to delete schedule' });
    }
  };

  const handleSendNow = async (scheduleId) => {
    try {
      const response = await reportAPI.sendReportNow(scheduleId);
      setMessage({ type: 'success', text: response.data.message });
      loadSchedules();
    } catch (error) {
      console.error('Error sending report:', error);
      setMessage({ type: 'error', text: 'Failed to send report' });
    }
  };

  const handleGenerateReport = async (reportType, period) => {
    try {
      setGenerating(true);
      const response = await reportAPI.generateReport(reportType, period);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evesix_${reportType}_report_${period}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setMessage({ type: 'success', text: 'Report downloaded successfully' });
    } catch (error) {
      console.error('Error generating report:', error);
      setMessage({ type: 'error', text: 'Failed to generate report' });
    } finally {
      setGenerating(false);
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      report_name: schedule.report_name,
      report_type: schedule.report_type,
      period: schedule.period,
      recipient_email: schedule.recipient_email,
      recipient_type: schedule.recipient_type,
      hospital_id: schedule.hospital_id,
      schedule_day: schedule.schedule_day,
      schedule_time: schedule.schedule_time,
      is_active: schedule.is_active
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      report_name: '',
      report_type: 'overview',
      period: 'week',
      recipient_email: '',
      recipient_type: 'hospital_admin',
      hospital_id: null,
      schedule_day: 'monday',
      schedule_time: '08:00',
      is_active: true
    });
  };

  const reportTypes = [
    { value: 'overview', label: 'Analytics Overview' },
    { value: 'robot', label: 'Robot Performance' },
    { value: 'operator', label: 'Operator Performance' },
    { value: 'ward', label: 'Ward/Bed Analysis' },
    { value: 'uvc', label: 'UV-C Analytics' }
  ];

  const periods = [
    { value: 'week', label: 'Week-to-Date' },
    { value: 'month', label: 'Month-to-Date' },
    { value: 'year', label: 'Year-to-Date' }
  ];

  const recipientTypes = [
    { value: 'hospital_admin', label: 'Hospital Admin' },
    { value: 'Regional _admin', label: 'Regional  Admin' },
    { value: 'group_ipp', label: 'Group IPP' }
  ];

  const days = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Report Scheduler</h2>
            <p className="text-gray-500 mt-1">Schedule and manage automated PDF reports</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mx-6 mt-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {message.text}
            <button onClick={() => setMessage(null)} className="float-right font-bold">x</button>
          </div>
        )}

        <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
          {/* Quick Generate Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Report Now</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {reportTypes.map(type => (
                <div key={type.value} className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 mb-3">{type.label}</h4>
                  <div className="flex flex-wrap gap-2">
                    {periods.map(period => (
                      <button
                        key={period.value}
                        onClick={() => handleGenerateReport(type.value, period.value)}
                        disabled={generating}
                        className="px-3 py-1.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                      >
                        {period.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Scheduled Reports Section */}
          <div className="mb-6 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Scheduled Reports</h3>
            <button
              onClick={() => { setShowForm(true); setEditingSchedule(null); resetForm(); }}
              className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors"
            >
              + New Schedule
            </button>
          </div>

          {/* Schedule Form */}
          {showForm && (
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                {editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
              </h4>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Report Name</label>
                  <input
                    type="text"
                    value={formData.report_name}
                    onChange={(e) => setFormData({ ...formData, report_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                  <select
                    value={formData.report_type}
                    onChange={(e) => setFormData({ ...formData, report_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    {reportTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                  <select
                    value={formData.period}
                    onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    {periods.map(period => (
                      <option key={period.value} value={period.value}>{period.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
                  <input
                    type="email"
                    value={formData.recipient_email}
                    onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Type</label>
                  <select
                    value={formData.recipient_type}
                    onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    {recipientTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Day</label>
                  <select
                    value={formData.schedule_day}
                    onChange={(e) => setFormData({ ...formData, schedule_day: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    {days.map(day => (
                      <option key={day.value} value={day.value}>{day.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Time</label>
                  <input
                    type="time"
                    value={formData.schedule_time}
                    onChange={(e) => setFormData({ ...formData, schedule_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
                <div className="md:col-span-2 lg:col-span-3 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingSchedule(null); }}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Schedules Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500">Loading schedules...</p>
            </div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <p className="text-gray-500">No scheduled reports yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Period</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Recipient</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Schedule</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Sent</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((schedule) => (
                    <tr key={schedule.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900 font-medium">{schedule.report_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 capitalize">{schedule.report_type}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 capitalize">{schedule.period}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{schedule.recipient_email}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 capitalize">{schedule.schedule_day} {schedule.schedule_time}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${schedule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {schedule.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {schedule.last_sent ? new Date(schedule.last_sent).toLocaleString() : 'Never'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSendNow(schedule.id)}
                            className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded text-xs font-medium transition-colors"
                          >
                            Send Now
                          </button>
                          <button
                            onClick={() => handleEdit(schedule)}
                            className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(schedule.id)}
                            className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded text-xs font-medium transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportScheduler;
